import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';

const DailyQuotas = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [quotasData, setQuotasData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editQuota, setEditQuota] = useState({
    pdv_id: null,
    prize_config_id: null,
    date: '',
    prize_name: '',
    prize_label: '',
    max_quantity: 0,
    used_quantity: 0
  });

  const loadSessions = async () => {
    try {
      const response = await api.get('/admin/sessions', { params: { brand: BRAND } });
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des sessions' });
    }
  };

  const loadQuotas = async (sessionId) => {
    if (!sessionId) { setQuotasData(null); return; }
    try {
      setLoading(true);
      const response = await api.get(`/admin/daily-quotas/${sessionId}`);
      setQuotasData(response.data);
    } catch (error) {
      console.error('Error loading quotas:', error);
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des quotas' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadSessions();
    setLoading(false);
  }, [user, navigate]);

  const handleSessionChange = (e) => {
    const sessionId = parseInt(e.target.value);
    const session = sessions.find(s => s.id === sessionId);
    setSelectedSession(session);
    loadQuotas(sessionId);
  };

  const handleEditQuota = (quota) => {
    setEditQuota({
      pdv_id: quotasData.pdv_id,
      prize_config_id: quota.prize_config_id,
      date: quota.date,
      prize_name: quota.prize_name,
      prize_label: quota.prize_label,
      max_quantity: quota.max_quantity,
      used_quantity: quota.used_quantity
    });
    setShowEditModal(true);
  };

  const handleSaveQuota = async () => {
    try {
      await api.put(`/admin/daily-quota/${editQuota.pdv_id}/${editQuota.prize_config_id}`, {
        quota_date: editQuota.date,
        max_quantity: editQuota.max_quantity,
        used_quantity: editQuota.used_quantity
      });
      setMessage({ type: 'success', text: 'Quota mis à jour avec succès' });
      setShowEditModal(false);
      loadQuotas(selectedSession.id);
    } catch (error) {
      console.error('Error updating quota:', error);
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    }
  };

  const getBadge = (used, max) => {
    const percentage = (used / max) * 100;
    if (percentage >= 100) return <span className="badge bg-danger">Épuisé</span>;
    if (percentage >= 80) return <span className="badge bg-warning text-dark">Critique</span>;
    if (percentage >= 50) return <span className="badge bg-info text-dark">Moyen</span>;
    return <span className="badge bg-success">OK</span>;
  };

  const getProgressBar = (used, max) => {
    const percentage = Math.min((used / max) * 100, 100);
    let color = 'bg-success';
    if (percentage >= 100) color = 'bg-danger';
    else if (percentage >= 80) color = 'bg-warning';
    else if (percentage >= 50) color = 'bg-info';
    return (
      <div className="progress" style={{ height: '6px', backgroundColor: '#dee2e6' }}>
        <div className={`progress-bar ${color}`} role="progressbar" style={{ width: `${percentage}%` }}></div>
      </div>
    );
  };

  // Light theme constants
  const navBg = '#1565c0';
  const cardBg = '#ffffff';
  const borderColor = '#dee2e6';
  const accentColor = '#1565c0';
  const modalBg = '#ffffff';
  const inputStyle = { background: '#fff', borderColor: '#dee2e6', color: '#1a1a1a' };
  const thColor = '#1565c0';

  if (loading && sessions.length === 0) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: '#f4f6f9' }}>
        <div className="spinner-border" role="status" style={{ color: accentColor }}></div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: '#f4f6f9' }}>
      {/* Header */}
      <nav className="navbar navbar-expand-lg navbar-dark sticky-top" style={{ background: navBg, padding: '12px 0' }}>
        <div className="container">
          <a className="navbar-brand fw-bold d-flex align-items-center" href="/dashboard" style={{ fontSize: '20px' }}>
            <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px', border: '2px solid #42a5f5' }}>
              <img src="/images/logo-supermont.png" alt="Supermont" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }} />
            </div>
            <span>Les Perles de Vie &mdash; Quotas</span>
          </a>
          <div className="d-flex align-items-center">
            <span className="text-white me-3">
              <i className="fas fa-user-shield me-2"></i>{user?.username}
            </span>
            <a href="/dashboard" className="btn btn-outline-light btn-sm">
              <i className="fas fa-arrow-left me-2"></i>Retour
            </a>
          </div>
        </div>
      </nav>

      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold mb-1" style={{ color: '#1a1a1a' }}>
              <i className="fas fa-calendar-alt me-2" style={{ color: accentColor }}></i>
              Gestion des Quotas Journaliers
            </h2>
            <p className="mb-0" style={{ color: '#555' }}>{sessions.length} session(s) configurée(s)</p>
          </div>
        </div>

        {/* Session Selector */}
        <div className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: cardBg, border: `1px solid ${borderColor}` }}>
          <div className="card-body p-3">
            <label className="form-label fw-bold" style={{ color: '#333' }}>Sélectionner une session</label>
            <select
              className="form-select"
              value={selectedSession?.id || ''}
              onChange={handleSessionChange}
              style={inputStyle}
            >
              <option value="">-- Choisir une session --</option>
              {sessions.map(session => (
                <option key={session.id} value={session.id}>
                  {session.pdv_name} - {session.name} ({session.start_time?.split('T')[0]} au {session.end_time?.split('T')[0]})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quotas Display */}
        {!quotasData ? (
          <div className="card border-0 shadow text-center p-5" style={{ borderRadius: '12px', background: cardBg, border: `1px solid ${borderColor}` }}>
            <i className="fas fa-calendar-plus fa-4x mb-3" style={{ opacity: 0.4, color: accentColor }}></i>
            <p className="mb-0" style={{ color: '#555' }}>Sélectionnez une session pour voir les quotas journaliers</p>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <h5 style={{ color: '#1a1a1a' }}>
                <i className="fas fa-store me-2" style={{ color: accentColor }}></i>
                {quotasData.pdv_name}
              </h5>
              <p className="mb-0" style={{ color: '#555' }}>
                <small>Du {quotasData.start_date} au {quotasData.end_date}</small>
              </p>
            </div>

            {quotasData.quotas_by_date.map((dayData, index) => (
              <div key={index} className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: cardBg, border: `1px solid ${borderColor}` }}>
                <div className="card-header p-3" style={{ background: 'rgba(66,165,245,0.1)', borderBottom: `1px solid ${borderColor}` }}>
                  <h6 className="mb-0 fw-bold" style={{ color: accentColor }}>
                    <i className="fas fa-calendar-day me-2"></i>
                    {new Date(dayData.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </h6>
                </div>
                <div className="card-body p-3">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                          <th style={{ color: thColor }}>Lot</th>
                          <th className="text-center" style={{ color: thColor }}>Quota</th>
                          <th className="text-center" style={{ color: thColor }}>Utilisé</th>
                          <th className="text-center" style={{ color: thColor }}>Restant</th>
                          <th className="text-center" style={{ color: thColor }}>Statut</th>
                          <th className="text-center" style={{ color: thColor }}>Progression</th>
                          <th className="text-center" style={{ color: thColor }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayData.quotas.map((quota, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div style={{ width: '16px', height: '16px', backgroundColor: quota.color, borderRadius: '4px', marginRight: '10px' }}></div>
                                <div>
                                  <div className="fw-semibold">{quota.prize_label}</div>
                                  <small className="text-muted">{quota.prize_name}</small>
                                </div>
                              </div>
                            </td>
                            <td className="text-center">{quota.max_quantity}</td>
                            <td className="text-center">{quota.used_quantity}</td>
                            <td className="text-center fw-bold" style={{ color: quota.remaining_quantity === 0 ? '#dc3545' : '#28a745' }}>
                              {quota.remaining_quantity}
                            </td>
                            <td className="text-center">{getBadge(quota.used_quantity, quota.max_quantity)}</td>
                            <td className="text-center" style={{ minWidth: '120px' }}>
                              {getProgressBar(quota.used_quantity, quota.max_quantity)}
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm"
                                style={{ background: 'linear-gradient(135deg, #1565c0, #0d47a1)', color: '#fff', border: 'none' }}
                                onClick={() => handleEditQuota(quota)}
                                title="Modifier le quota"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                        {/* Ligne Réessayez encore */}
                        <tr style={{ background: '#fff8f8' }}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div style={{ width: '16px', height: '16px', backgroundColor: '#e57373', borderRadius: '4px', marginRight: '10px' }}></div>
                              <div>
                                <div className="fw-semibold" style={{ color: '#c62828' }}>Réessayez encore</div>
                                <small className="text-muted">Tirages perdants</small>
                              </div>
                            </div>
                          </td>
                          <td className="text-center text-muted">—</td>
                          <td className="text-center fw-bold" style={{ color: '#c62828' }}>{dayData.try_again_count ?? 0}</td>
                          <td className="text-center text-muted">—</td>
                          <td className="text-center">
                            <span className="badge" style={{ background: '#ffebee', color: '#c62828' }}>
                              <i className="fas fa-rotate-right me-1"></i>Perdants
                            </span>
                          </td>
                          <td className="text-center text-muted">—</td>
                          <td className="text-center text-muted">—</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Edit Quota Modal */}
        {showEditModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ background: modalBg, border: `1px solid ${borderColor}`, boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
                <div className="modal-header border-bottom">
                  <h5 className="modal-title" style={{ color: '#1a1a1a' }}>
                    <i className="fas fa-edit me-2" style={{ color: accentColor }}></i>
                    Modifier le quota journalier
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#333' }}>Date</label>
                    <input type="date" className="form-control" value={editQuota.date} disabled style={inputStyle} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#333' }}>Lot</label>
                    <input type="text" className="form-control" value={editQuota.prize_label} disabled style={inputStyle} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#333' }}>Quota maximum</label>
                    <input
                      type="number" className="form-control"
                      value={editQuota.max_quantity}
                      onChange={(e) => setEditQuota({ ...editQuota, max_quantity: parseInt(e.target.value) || 0 })}
                      min="0" style={inputStyle}
                    />
                    <small className="text-muted">Nombre maximum de lots disponibles pour cette journée</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#333' }}>Quantité utilisée</label>
                    <input
                      type="number" className="form-control"
                      value={editQuota.used_quantity}
                      onChange={(e) => setEditQuota({ ...editQuota, used_quantity: parseInt(e.target.value) || 0 })}
                      min="0" style={inputStyle}
                    />
                    <small className="text-muted">Nombre de lots déjà distribués aujourd'hui</small>
                  </div>
                  <div className="alert alert-info" style={{ background: 'rgba(21,101,192,0.08)', borderColor: 'rgba(21,101,192,0.2)' }}>
                    <small style={{ color: '#555' }}>
                      <i className="fas fa-info-circle me-2"></i>
                      Stock restant : <strong style={{ color: '#1a1a1a' }}>{Math.max(0, editQuota.max_quantity - editQuota.used_quantity)}</strong>
                    </small>
                  </div>
                </div>
                <div className="modal-footer border-top-0">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Annuler</button>
                  <button type="button" className="btn btn-primary" onClick={handleSaveQuota}
                    style={{ background: 'linear-gradient(135deg, #1976d2, #1565c0)', border: 'none' }}>
                    <i className="fas fa-save me-2"></i>Enregistrer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyQuotas;
